import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { aiAgentsApi } from 'apis/aiAgents';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { askSidekick } from 'utils/sidekick';

import { createAiAgentDebugPrompt } from './sidekickDebugButton.utils';

const LABEL = 'Debug this AI reply';
const ASK_TITLE = 'Debug this AI reply';
const ASK_LABEL = 'What appears wrong with this reply?';
const ASK_PLACEHOLDER = 'For example: it cited the wrong refund policy.';

type SidekickDebugButtonProps = {
  /** The id of the user that sent the message. */
  authorId: string;
  /** The id of the conversation the message belongs to. */
  csid: string;
  /** The id of the message. */
  messageId: string;
  /** When the message was sent. */
  date?: string;
};

/** Starts a Sidekick debugging session for a message sent by an AI agent. */
export const SidekickDebugButton = ({
  authorId,
  csid,
  messageId,
  date,
}: SidekickDebugButtonProps): JSX.Element | null => {
  const flags = useFeatureFlag(['mim-sidekick']);

  const { data } = aiAgentsApi.useReadAiAgentsListQuery(
    {},
    { skip: !flags.mimSidekick },
  );

  const isAiAgentAuthor = useMemo(
    () =>
      (data?.data?.agents ?? []).some(
        (agent) => agent.systemUserId === authorId,
      ),
    [data, authorId],
  );

  if (!flags.mimSidekick || !isAiAgentAuthor) return null;

  return (
    <Tooltip wrap="span" content={LABEL}>
      <IconButton
        variant="secondary"
        icon="mim-sidekick"
        aria-label={LABEL}
        onClick={() =>
          askSidekick({
            title: ASK_TITLE,
            label: ASK_LABEL,
            placeholder: ASK_PLACEHOLDER,
            createPrompt: (answer) =>
              createAiAgentDebugPrompt({ csid, messageId, date, note: answer }),
          })
        }
      />
    </Tooltip>
  );
};
